import React, { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
    FolderOpen, Search, Download, X, ChevronLeft,
    ZoomIn, ZoomOut, Share2, RefreshCw, ImageOff,
    Folder, AlertTriangle, ExternalLink
} from 'lucide-react'
import { getDocuments } from '../../services/staffServices'
import StaffLayout from './components/StaffLayout'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso) {
    if (!iso) return '—'
    try {
        const d = new Date(iso)
        const today = new Date()
        const yesterday = new Date(); yesterday.setDate(today.getDate() - 1)
        if (d.toDateString() === today.toDateString()) return 'Today'
        if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
        return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    } catch { return iso.slice(0, 10) }
}

function formatDateTime(iso) {
    if (!iso) return '—'
    try {
        const d = new Date(iso)
        return `${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`
    } catch { return iso.slice(0, 10) }
}

function groupByDate(items) {
    const groups = {}
    items.forEach(item => {
        const key = formatDate(item.created_at)
        if (!groups[key]) groups[key] = []
        groups[key].push(item)
    })
    return Object.entries(groups)
}

// ── Image Thumbnail ───────────────────────────────────────────────────────────

function Thumbnail({ doc, onClick }) {
    const [state, setState] = useState('loading') // loading | loaded | error

    return (
        <button
            onClick={onClick}
            className="relative aspect-square overflow-hidden rounded-lg bg-slate-800 hover:opacity-90 transition-opacity group focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
            {state === 'loading' && (
                <div className="absolute inset-0 bg-slate-800 animate-pulse" />
            )}
            {state === 'error' ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-800 gap-1">
                    <ImageOff size={20} className="text-slate-600" />
                    <span className="text-[10px] text-slate-600">No image</span>
                </div>
            ) : (
                <img
                    src={doc.url}
                    alt={doc.label}
                    className="w-full h-full object-cover"
                    onLoad={() => setState('loaded')}
                    onError={() => setState('error')}
                />
            )}
            {/* Label gradient */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent pt-4 pb-1.5 px-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-[10px] text-white truncate leading-tight">{doc.label}</p>
            </div>
        </button>
    )
}

// ── Folder Card ───────────────────────────────────────────────────────────────

function FolderCard({ name, count, coverUrl, onClick }) {
    const [imgErr, setImgErr] = useState(false)

    return (
        <button
            onClick={onClick}
            className="bg-slate-900 border border-white/5 rounded-xl overflow-hidden hover:border-white/10 hover:scale-[1.02] active:scale-[0.98] transition-all text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
            {/* Cover */}
            <div className="relative aspect-[4/3] bg-slate-800">
                {coverUrl && !imgErr ? (
                    <>
                        <img
                            src={coverUrl}
                            alt={name}
                            className="w-full h-full object-cover"
                            onError={() => setImgErr(true)}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    </>
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Folder size={36} className="text-slate-600" />
                    </div>
                )}
                {/* Count badge */}
                <span className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded-lg">
                    {count}
                </span>
            </div>
            {/* Name */}
            <div className="px-3 py-2.5">
                <p className="text-sm font-semibold text-white truncate">{name}</p>
                <p className="text-[11px] text-slate-500">{count} item{count !== 1 ? 's' : ''}</p>
            </div>
        </button>
    )
}

// ── Full-screen Viewer ────────────────────────────────────────────────────────

function ImageViewer({ items, startIndex, onClose }) {
    const [current, setCurrent] = useState(startIndex)
    const [zoom, setZoom] = useState(1)
    const [barsVisible, setBarsVisible] = useState(true)

    const doc = items[current]

    // keyboard nav
    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'ArrowRight') setCurrent(c => Math.min(c + 1, items.length - 1))
            else if (e.key === 'ArrowLeft') setCurrent(c => Math.max(c - 1, 0))
            else if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [items.length, onClose])

    // reset zoom on image change
    useEffect(() => { setZoom(1) }, [current])

    const handleCopyLink = () => {
        navigator.clipboard.writeText(doc.url)
        // We don't import toast here to keep this component lean; use alert as fallback
        // toast would work if imported
    }

    return (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
            {/* Top bar */}
            {barsVisible && (
                <div className="absolute top-0 inset-x-0 z-10 bg-gradient-to-b from-black/80 to-transparent px-4 pt-3 pb-8">
                    <div className="flex items-center gap-3">
                        <button onClick={onClose} className="text-white hover:text-slate-300 transition p-1 rounded-lg hover:bg-white/10">
                            <X size={20} />
                        </button>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-white truncate">{doc.source}</p>
                            <p className="text-xs text-white/60">{current + 1} / {items.length}</p>
                        </div>
                        {/* Zoom controls */}
                        <button onClick={() => setZoom(z => Math.min(z + 0.5, 4))} className="text-white hover:text-slate-300 p-1.5 rounded-lg hover:bg-white/10 transition">
                            <ZoomIn size={18} />
                        </button>
                        <button onClick={() => setZoom(z => Math.max(z - 0.5, 0.5))} className="text-white hover:text-slate-300 p-1.5 rounded-lg hover:bg-white/10 transition">
                            <ZoomOut size={18} />
                        </button>
                        {/* Share / copy link */}
                        <button
                            onClick={() => { navigator.clipboard?.writeText(doc.url) }}
                            className="text-white hover:text-slate-300 p-1.5 rounded-lg hover:bg-white/10 transition"
                            title="Copy link"
                        >
                            <Share2 size={18} />
                        </button>
                        {/* Download */}
                        <a
                            href={doc.url}
                            download
                            target="_blank"
                            rel="noreferrer"
                            className="text-white hover:text-slate-300 p-1.5 rounded-lg hover:bg-white/10 transition"
                            title="Download"
                        >
                            <Download size={18} />
                        </a>
                        {/* Open in new tab */}
                        <a
                            href={doc.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-white hover:text-slate-300 p-1.5 rounded-lg hover:bg-white/10 transition"
                            title="Open in browser"
                        >
                            <ExternalLink size={18} />
                        </a>
                    </div>
                </div>
            )}

            {/* Image */}
            <div
                className="flex-1 flex items-center justify-center overflow-hidden cursor-pointer"
                onClick={() => setBarsVisible(v => !v)}
            >
                <img
                    src={doc.url}
                    alt={doc.label}
                    className="max-w-full max-h-full object-contain select-none transition-transform duration-200"
                    style={{ transform: `scale(${zoom})` }}
                    draggable={false}
                />
            </div>

            {/* Bottom bar */}
            {barsVisible && (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/85 to-transparent px-4 pb-6 pt-10">
                    <div className="flex items-center justify-between mb-3">
                        {/* Prev */}
                        <button
                            onClick={() => setCurrent(c => Math.max(c - 1, 0))}
                            disabled={current === 0}
                            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white text-sm transition"
                        >
                            ← Prev
                        </button>
                        {/* Dots (max 15) */}
                        {items.length <= 15 && (
                            <div className="flex gap-1.5 items-center">
                                {items.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setCurrent(i)}
                                        className={`rounded-full transition-all ${i === current ? 'w-4 h-2 bg-white' : 'w-2 h-2 bg-white/30'}`}
                                    />
                                ))}
                            </div>
                        )}
                        {/* Next */}
                        <button
                            onClick={() => setCurrent(c => Math.min(c + 1, items.length - 1))}
                            disabled={current === items.length - 1}
                            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white text-sm transition"
                        >
                            Next →
                        </button>
                    </div>
                    {/* Meta */}
                    <div className="flex flex-wrap gap-3 text-xs text-white/70">
                        <span className="bg-blue-600/80 text-white px-2.5 py-1 rounded-full text-[11px] font-medium">{doc.label}</span>
                        <span>By {doc.uploaded_by || '—'}</span>
                        <span>{formatDateTime(doc.created_at)}</span>
                    </div>
                </div>
            )}
        </div>
    )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Documents() {
    const [openFolder, setOpenFolder] = useState(null) // null = folder grid view
    const [viewerIndex, setViewerIndex] = useState(-1)
    const [search, setSearch] = useState('')

    const { data: allDocs = [], isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ['documents'],
        queryFn: getDocuments,
        staleTime: 5 * 60 * 1000,
    })

    // Build folders: { name → [docs] }, sorted by count desc
    const folders = React.useMemo(() => {
        const map = {}
        allDocs.forEach(doc => {
            const folder = doc.source.split('•')[0].trim()
            if (!map[folder]) map[folder] = []
            map[folder].push(doc)
        })
        return Object.entries(map)
            .map(([name, docs]) => ({ name, docs, count: docs.length }))
            .sort((a, b) => b.count - a.count)
    }, [allDocs])

    // Items inside open folder, filtered by search
    const folderDocs = React.useMemo(() => {
        if (!openFolder) return []
        const docs = folders.find(f => f.name === openFolder)?.docs || []
        if (!search) return docs
        return docs.filter(d =>
            d.label.toLowerCase().includes(search.toLowerCase()) ||
            d.source.toLowerCase().includes(search.toLowerCase()) ||
            d.uploaded_by?.toLowerCase().includes(search.toLowerCase())
        )
    }, [openFolder, folders, search])

    // Grouped by date for folder view
    const groupedDocs = React.useMemo(() => groupByDate(folderDocs), [folderDocs])

    const handleBack = () => {
        if (openFolder) { setOpenFolder(null); setSearch('') }
    }

    // Filtered folders by search
    const filteredFolders = React.useMemo(() => {
        if (!search || openFolder) return folders
        return folders.filter(f => f.name.toLowerCase().includes(search.toLowerCase()))
    }, [folders, search, openFolder])

    return (
        <StaffLayout>
            <div className="space-y-5">
                {/* Header */}
                <div className="flex items-center gap-3">
                    {openFolder && (
                        <button
                            onClick={handleBack}
                            className="text-slate-400 hover:text-white transition p-1.5 rounded-lg hover:bg-white/5"
                        >
                            <ChevronLeft size={20} />
                        </button>
                    )}
                    <div className="flex-1 min-w-0">
                        <h1 className="text-xl font-bold text-white truncate">
                            {openFolder || 'Documents'}
                            {!isLoading && (
                                <span className="text-slate-500 font-normal text-base ml-2">
                                    ({openFolder
                                        ? `${folderDocs.length} item${folderDocs.length !== 1 ? 's' : ''}`
                                        : `${folders.length} folder${folders.length !== 1 ? 's' : ''}, ${allDocs.length} files`})
                                </span>
                            )}
                        </h1>
                        {openFolder && (
                            <p className="text-xs text-slate-500 mt-0.5">
                                Images from bookings, BSNL, deliveries, loans & more
                            </p>
                        )}
                    </div>
                    <button
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="text-slate-400 hover:text-white transition p-1.5 rounded-lg hover:bg-white/5 disabled:opacity-50"
                        title="Refresh"
                    >
                        <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
                    </button>
                </div>

                {/* Search */}
                {(openFolder || folders.length > 0) && (
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder={openFolder ? 'Search images…' : 'Search folders…'}
                            className="w-full bg-slate-900 border border-white/5 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                        />
                        {search && (
                            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                                <X size={14} />
                            </button>
                        )}
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400">
                        <AlertTriangle size={16} className="shrink-0" />
                        <p className="text-sm">{error.message || 'Failed to load documents'}</p>
                        <button onClick={() => refetch()} className="ml-auto text-xs underline hover:no-underline">Retry</button>
                    </div>
                )}

                {/* Loading skeleton */}
                {isLoading && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {Array(8).fill(0).map((_, i) => (
                            <div key={i} className="rounded-xl overflow-hidden animate-pulse">
                                <div className="aspect-[4/3] bg-slate-800" />
                                <div className="p-3 bg-slate-900 space-y-1.5">
                                    <div className="h-3 bg-slate-800 rounded w-3/4" />
                                    <div className="h-2.5 bg-slate-800 rounded w-1/3" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Folder grid ── */}
                {!isLoading && !openFolder && (
                    filteredFolders.length === 0 ? (
                        <div className="bg-slate-900 border border-white/5 rounded-xl p-16 flex flex-col items-center gap-4 text-center">
                            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center">
                                <FolderOpen size={28} className="text-amber-500" />
                            </div>
                            <div>
                                <p className="text-white font-semibold text-lg">No documents yet</p>
                                <p className="text-slate-500 text-sm mt-1 max-w-xs">
                                    Images from bookings, BSNL connections, deliveries, loans, and tasks appear here automatically.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {filteredFolders.map(folder => (
                                <FolderCard
                                    key={folder.name}
                                    name={folder.name}
                                    count={folder.count}
                                    coverUrl={folder.docs[0]?.url}
                                    onClick={() => { setOpenFolder(folder.name); setSearch('') }}
                                />
                            ))}
                        </div>
                    )
                )}

                {/* ── Inside folder — date-grouped photo grid ── */}
                {!isLoading && openFolder && (
                    folderDocs.length === 0 ? (
                        <div className="bg-slate-900 border border-white/5 rounded-xl p-12 flex flex-col items-center gap-3 text-center">
                            <ImageOff size={32} className="text-slate-600" />
                            <p className="text-slate-400 font-medium">{search ? 'No results found' : 'No images in this folder'}</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {groupedDocs.map(([date, items]) => (
                                <div key={date}>
                                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3 px-0.5">{date}</p>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-1.5">
                                        {items.map((doc, idx) => {
                                            // global index in folderDocs for viewer
                                            const globalIdx = folderDocs.indexOf(doc)
                                            return (
                                                <Thumbnail
                                                    key={`${doc.url}-${idx}`}
                                                    doc={doc}
                                                    onClick={() => setViewerIndex(globalIdx)}
                                                />
                                            )
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                )}
            </div>

            {/* Full-screen viewer */}
            {viewerIndex >= 0 && folderDocs.length > 0 && (
                <ImageViewer
                    items={folderDocs}
                    startIndex={viewerIndex}
                    onClose={() => setViewerIndex(-1)}
                />
            )}
        </StaffLayout>
    )
}
